/**
 * Quake 3 weapon set, Lightning edition.
 *  - Lightning Gun (⚡ signature): continuous beam, 8 dmg/tick @ 20 ticks/s, range 24m, procedural bolt
 *  - Rocket Launcher: projectile 25 m/s, 100 dmg, splash radius 4m — rocket jumps work
 *  - Railgun: hitscan 100 dmg, 1.5s reload, spiral trail
 *  - Shotgun: 11 pellets × 10 dmg spread
 * Damage is dealt by the game layer via callbacks (bots + player share one interface).
 */

import * as THREE from 'three';
import { Box } from './map';
import { FX, LightHandle } from '../render/fx';
import { SOLID } from '../render/materials';

export type WeaponId = 'lightning' | 'rocket' | 'rail' | 'shotgun';

export interface WeaponDef {
  id: WeaponId;
  name: string;
  short: string;
  damage: number;
  fireInterval: number; // seconds between shots (ticks for LG)
  range: number;
  ammoMax: number;
  ammoStart: number;
  color: number;
  hitscan: boolean;
  pellets?: number;
  spread?: number;
  splashRadius?: number;
  projectileSpeed?: number;
}

export const WEAPONS: Record<WeaponId, WeaponDef> = {
  lightning: { id: 'lightning', name: 'Lightning Gun', short: 'LG', damage: 8, fireInterval: 0.05, range: 24, ammoMax: 200, ammoStart: 120, color: 0x9be7ff, hitscan: true },
  rocket:    { id: 'rocket', name: 'Rocket Launcher', short: 'RL', damage: 100, fireInterval: 0.8, range: 200, ammoMax: 25, ammoStart: 10, color: 0xff7a00, hitscan: false, splashRadius: 4.2, projectileSpeed: 26 },
  rail:      { id: 'rail', name: 'Railgun', short: 'RG', damage: 100, fireInterval: 1.5, range: 400, ammoMax: 20, ammoStart: 10, color: 0x2fff9a, hitscan: true },
  shotgun:   { id: 'shotgun', name: 'Shotgun', short: 'SG', damage: 10, fireInterval: 1.0, range: 60, ammoMax: 50, ammoStart: 20, color: 0xffe08a, hitscan: true, pellets: 11, spread: 0.06 },
};

export const WEAPON_ORDER: WeaponId[] = ['lightning', 'rocket', 'rail', 'shotgun'];

// ---------------------------------------------------------------- targets

export interface Hittable {
  id: string;
  /** AABB for hit tests */
  hitBox(out: { min: THREE.Vector3; max: THREE.Vector3 }): void;
  alive(): boolean;
}

export interface HitResult {
  point: THREE.Vector3;
  normal: THREE.Vector3;
  distance: number;
  target: Hittable | null;
}

const _box = { min: new THREE.Vector3(), max: new THREE.Vector3() };

/**
 * Temporaires de `trace()`. Une petite pile de profondeur 4 : `trace` n'est
 * jamais récursif aujourd'hui, mais la pile garantit que deux appels imbriqués
 * (via un callback) ne s'écrasent pas mutuellement.
 */
const _traceTmp = Array.from({ length: 4 }, () => ({ n: new THREE.Vector3() }));
let _traceDepth = 0;

/** Ray vs AABB (slab). Returns t or -1. */
export function rayAABB(origin: THREE.Vector3, dir: THREE.Vector3, min: THREE.Vector3, max: THREE.Vector3, maxT: number, normalOut?: THREE.Vector3): number {
  let tmin = 0, tmax = maxT;
  let axis = -1, sign = 0;
  for (let i = 0; i < 3; i++) {
    const o = origin.getComponent(i), d = dir.getComponent(i);
    const lo = min.getComponent(i), hi = max.getComponent(i);
    if (Math.abs(d) < 1e-9) {
      if (o < lo || o > hi) return -1;
    } else {
      const inv = 1 / d;
      let t1 = (lo - o) * inv, t2 = (hi - o) * inv;
      let s = -1;
      if (t1 > t2) { const tmp = t1; t1 = t2; t2 = tmp; s = 1; }
      if (t1 > tmin) { tmin = t1; axis = i; sign = s; }
      if (t2 < tmax) tmax = t2;
      if (tmin > tmax) return -1;
    }
  }
  if (normalOut) {
    normalOut.set(0, 0, 0);
    if (axis >= 0) normalOut.setComponent(axis, sign);
    else normalOut.set(0, 1, 0);
  }
  return tmin;
}

/** Trace a ray against world solids and hittable targets (excluding `skip`). */
export function trace(origin: THREE.Vector3, dir: THREE.Vector3, range: number, solids: Box[], targets: Hittable[], skip?: Hittable): HitResult {
  let bestT = range;
  let bestTarget: Hittable | null = null;
  const n = _traceTmp[Math.min(_traceDepth++, _traceTmp.length - 1)].n;
  const normal = new THREE.Vector3(0, 1, 0);
  try {
    for (const s of solids) {
      const t = rayAABB(origin, dir, s.min, s.max, bestT, n);
      if (t >= 0 && t < bestT) { bestT = t; normal.copy(n); bestTarget = null; }
    }
    for (const tg of targets) {
      if (tg === skip || !tg.alive()) continue;
      tg.hitBox(_box);
      const t = rayAABB(origin, dir, _box.min, _box.max, bestT, n);
      if (t >= 0 && t < bestT) { bestT = t; normal.copy(n); bestTarget = tg; }
    }
  } finally {
    _traceDepth--;
  }
  return { point: origin.clone().addScaledVector(dir, bestT), normal, distance: bestT, target: bestTarget };
}

/**
 * Variante « visibilité seule » : pas d'allocation du tout, pas de cibles.
 * Utilisée par la ligne de vue des bots (des milliers d'appels par seconde).
 */
const _losN = new THREE.Vector3();
export function traceBlocked(origin: THREE.Vector3, dir: THREE.Vector3, range: number, solids: Box[]): boolean {
  const n = _losN;
  for (const s of solids) {
    const t = rayAABB(origin, dir, s.min, s.max, range, n);
    if (t >= 0 && t < range) return true;
  }
  return false;
}

// ---------------------------------------------------------------- effets (délégués au moteur FX)

/**
 * Éclair du Lightning Gun — deux polylignes (cœur blanc + halo coloré) qui
 * suivent le rayon. Aucune PointLight dynamique : les flashes passent par le
 * pool du moteur FX (voir `FX.light` / `FX.impact`), sinon three.js recompile
 * tous les matériaux dès que le nombre de lumières change.
 */
export class LightningBolt {
  private readonly core: THREE.Line;
  private readonly halo: THREE.Line;
  private positions: Float32Array;
  private readonly segments = 24;

  constructor(scene: THREE.Scene, color: number) {
    this.positions = new Float32Array((this.segments + 1) * 3);
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(this.positions, 3));
    (geo.getAttribute('position') as THREE.BufferAttribute).setUsage(THREE.DynamicDrawUsage);
    this.core = new THREE.Line(geo, new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.95, blending: THREE.AdditiveBlending, depthWrite: false }));
    this.halo = new THREE.Line(geo, new THREE.LineBasicMaterial({ color, transparent: true, opacity: 0.5, blending: THREE.AdditiveBlending, depthWrite: false }));
    this.core.renderOrder = 20; this.halo.renderOrder = 19;
    this.core.frustumCulled = false; this.halo.frustumCulled = false;
    this.core.visible = false; this.halo.visible = false;
    scene.add(this.core, this.halo);
  }

  /** Reconstruit l'éclair dentelé entre a et b (appelé à chaque frame de tir). */
  set(a: THREE.Vector3, b: THREE.Vector3): void {
    const dir = _v1.subVectors(b, a);
    const len = dir.length();
    if (len < 1e-4) { this.hide(); return; }
    dir.multiplyScalar(1 / len);
    const up = Math.abs(dir.y) < 0.9 ? _v2.set(0, 1, 0) : _v2.set(1, 0, 0);
    const p1 = _v3.crossVectors(dir, up).normalize();
    const p2 = _v4.crossVectors(dir, p1).normalize();
    for (let i = 0; i <= this.segments; i++) {
      const t = i / this.segments;
      const amp = Math.sin(t * Math.PI) * Math.min(0.32, len * 0.028);
      const j1 = (Math.random() - 0.5) * 2 * amp;
      const j2 = (Math.random() - 0.5) * 2 * amp;
      this.positions[i * 3] = a.x + dir.x * len * t + p1.x * j1 + p2.x * j2;
      this.positions[i * 3 + 1] = a.y + dir.y * len * t + p1.y * j1 + p2.y * j2;
      this.positions[i * 3 + 2] = a.z + dir.z * len * t + p1.z * j1 + p2.z * j2;
    }
    (this.core.geometry.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
    this.core.geometry.computeBoundingSphere();
    (this.halo.material as THREE.LineBasicMaterial).opacity = 0.35 + Math.random() * 0.3;
    this.core.visible = true; this.halo.visible = true;
  }

  hide(): void { this.core.visible = false; this.halo.visible = false; }

  dispose(scene: THREE.Scene): void {
    scene.remove(this.core, this.halo);
    this.core.geometry.dispose();
    (this.core.material as THREE.Material).dispose();
    (this.halo.material as THREE.Material).dispose();
  }
}

const _v1 = new THREE.Vector3(), _v2 = new THREE.Vector3(), _v3 = new THREE.Vector3(), _v4 = new THREE.Vector3();

/**
 * Géométries et matériaux de roquette, fabriqués une seule fois puis partagés
 * par toutes les instances (un tir = 0 allocation GPU). Ils ne sont JAMAIS
 * disposés : `Rocket.dispose()` se contente de retirer le mesh de la scène.
 */
interface RocketAssets {
  body: THREE.CylinderGeometry; nose: THREE.ConeGeometry; tip: THREE.SphereGeometry;
  ring: THREE.TorusGeometry; ring2: THREE.TorusGeometry; fin: THREE.BoxGeometry;
  exhaust: THREE.CylinderGeometry; plume: THREE.ConeGeometry;
  hull: THREE.MeshStandardMaterial; steel: THREE.MeshStandardMaterial; band: THREE.MeshStandardMaterial;
  tipMat: THREE.MeshStandardMaterial; exhaustMat: THREE.MeshStandardMaterial; plumeMat: THREE.MeshBasicMaterial;
}
let _rocketAssets: RocketAssets | null = null;
function rocketAssets(): RocketAssets {
  return _rocketAssets ??= {
    body: new THREE.CylinderGeometry(0.075, 0.085, 0.46, 12),
    nose: new THREE.ConeGeometry(0.075, 0.22, 12),
    tip: new THREE.SphereGeometry(0.026, 8, 6),
    ring: new THREE.TorusGeometry(0.086, 0.014, 6, 14),
    ring2: new THREE.TorusGeometry(0.088, 0.012, 6, 14),
    fin: new THREE.BoxGeometry(0.012, 0.11, 0.16),
    exhaust: new THREE.CylinderGeometry(0.062, 0.078, 0.09, 12, 1, true),
    plume: new THREE.ConeGeometry(0.07, 0.34, 10, 1, true),
    hull: SOLID.gunmetal(),
    steel: SOLID.brushed(),
    band: SOLID.orange(),
    tipMat: SOLID.glow(0xff5a12, 4),
    exhaustMat: SOLID.glow(0xff8a30, 3),
    plumeMat: new THREE.MeshBasicMaterial({ color: 0xffc266, transparent: true, opacity: 0.75, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide }),
  };
}

/**
 * Roquette : maillage détaillé (corps, ogive, ailerons, tuyère émissive),
 * lumière moteur empruntée au pool FX, fumée via `FX.rocketTrail`.
 */
export class Rocket {
  public readonly mesh: THREE.Group;
  public position: THREE.Vector3;
  public velocity: THREE.Vector3;
  public alive = true;
  public age = 0;
  public readonly ownerId: string;

  private fx: FX | null;
  private lightH: LightHandle | null = null;
  private trailAcc = { t: 0 };
  private back = new THREE.Vector3();
  private exhaust: THREE.Mesh;
  private exhaustMat: THREE.MeshStandardMaterial;
  private plume: THREE.Mesh;
  private spin = 0;
  private disposed = false;

  constructor(scene: THREE.Scene, origin: THREE.Vector3, dir: THREE.Vector3, ownerId: string, fx?: FX) {
    this.ownerId = ownerId;
    this.fx = fx ?? null;
    this.position = origin.clone();
    this.velocity = dir.clone().multiplyScalar(WEAPONS.rocket.projectileSpeed!);
    this.back.copy(dir).negate();

    const R = rocketAssets();
    const g = new THREE.Group();

    const body = new THREE.Mesh(R.body, R.hull);
    body.rotation.x = Math.PI / 2;
    const nose = new THREE.Mesh(R.nose, R.steel);
    nose.rotation.x = -Math.PI / 2; nose.position.z = -0.34;
    const tip = new THREE.Mesh(R.tip, R.tipMat);
    tip.position.z = -0.45;
    const ring = new THREE.Mesh(R.ring, R.band);
    ring.position.z = -0.12;
    const ring2 = new THREE.Mesh(R.ring2, R.band);
    ring2.position.z = 0.1;
    // ailerons
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2;
      const fin = new THREE.Mesh(R.fin, R.steel);
      fin.position.set(Math.cos(a) * 0.1, Math.sin(a) * 0.1, 0.2);
      fin.rotation.z = a;
      g.add(fin);
    }
    // tuyère + panache : matériau propre à l'instance (emissiveIntensity animée)
    this.exhaustMat = R.exhaustMat.clone();
    this.exhaust = new THREE.Mesh(R.exhaust, this.exhaustMat);
    this.exhaust.rotation.x = Math.PI / 2; this.exhaust.position.z = 0.27;
    this.plume = new THREE.Mesh(R.plume, R.plumeMat);
    this.plume.rotation.x = -Math.PI / 2; this.plume.position.z = 0.44;
    g.add(body, nose, tip, ring, ring2, this.exhaust, this.plume);
    this.mesh = g;
    this.mesh.position.copy(this.position);
    this.mesh.lookAt(this.position.clone().add(dir));
    scene.add(this.mesh);

    if (this.fx) this.lightH = this.fx.lightHandle(0xff7a2a, 11);
  }

  /** Le FX peut aussi être fourni au premier pas (compat game.ts). */
  attach(fx: FX): void {
    if (this.fx) return;
    this.fx = fx;
    this.lightH = fx.lightHandle(0xff7a2a, 11);
  }

  step(dt: number, fx?: FX): void {
    if (fx && !this.fx) this.attach(fx);
    this.age += dt;
    this.position.addScaledVector(this.velocity, dt);
    this.mesh.position.copy(this.position);
    this.spin += dt * 9;
    this.mesh.rotateZ(dt * 9);
    const pulse = 0.85 + Math.sin(this.spin * 3) * 0.15;
    this.plume.scale.set(pulse, 1 + Math.sin(this.spin * 5) * 0.25, pulse);
    this.exhaustMat.emissiveIntensity = 2.4 + Math.sin(this.spin * 7) * 0.9;
    const engine = this.fx;
    if (engine) {
      engine.rocketTrail(this.position, this.back, dt, this.trailAcc);
      this.lightH?.set(this.position, 22 + Math.random() * 8);
    }
  }

  /**
   * Idempotent : le slot de lumière FX est rendu exactement une fois, même si
   * `dispose` est rappelé (fin de match, nettoyage des roquettes en vol).
   * Géométries et matériaux sont partagés → on ne dispose que le clone local.
   */
  dispose(scene: THREE.Scene): void {
    if (this.disposed) return;
    this.disposed = true;
    this.alive = false;
    this.lightH?.release();
    this.lightH = null;
    scene.remove(this.mesh);
    this.exhaustMat.dispose();
  }
}
